"use client";

import { useSearchParams } from "next/navigation";
import { type ReactNode, Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MUSIC_CASE_SIZE_CSS,
  MUSIC_STAGE_HEIGHT_CSS,
} from "@/features/profile/components/profile-music/music-stage";
import { resolveProfileNavigation } from "@/lib/profile-navigation";
import { cn } from "@/lib/utils";
import { ProfileBuzzSkeleton } from "./profile-buzz.skeleton";
import { PROFILE_DESKTOP } from "./profile-page-desktop";

/**
 * Mirrors `ProfilePageView` in page mode on the Details tab, at every width:
 * the phone column (fixed header, square hero, sticky tabs, details) and, from
 * `wide`, the two-column frame (sticky side column with a square hero and the
 * summary, tabs and the two-up details grid on the right). It spreads the same
 * `PROFILE_DESKTOP` tokens as the real view, so the two cannot drift apart.
 *
 * Shared by the own-profile and public-profile Suspense fallbacks; they differ
 * only in the header's leading back button and trailing actions.
 */
type ProfilePageSkeletonProps = {
  withBackButton?: boolean;
  headerActions: ReactNode;
};

export function ProfilePageSkeleton(props: ProfilePageSkeletonProps) {
  return (
    <Suspense fallback={<ProfilePageSkeletonLayout {...props} />}>
      <QueryAwareProfileSkeleton {...props} />
    </Suspense>
  );
}

function QueryAwareProfileSkeleton(props: ProfilePageSkeletonProps) {
  const params = useSearchParams();
  return (
    <ProfilePageSkeletonLayout
      {...props}
      navigation={resolveProfileNavigation(params.toString())}
    />
  );
}

function ProfilePageSkeletonLayout({
  withBackButton = false,
  headerActions,
  navigation = resolveProfileNavigation(""),
}: ProfilePageSkeletonProps & {
  navigation?: ReturnType<typeof resolveProfileNavigation>;
}) {
  return (
    <div className="bg-background pb-4">
      {/* Fixed header: [back] + title (text-lg) + header actions */}
      <header
        className={cn(
          "fixed top-0 right-0 left-0 z-50 border-b border-border/50 bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-md supports-backdrop-filter:bg-background/60",
          PROFILE_DESKTOP.barColumn,
        )}
      >
        <div
          className={cn(
            "mx-auto flex h-14 max-w-3xl items-center justify-between gap-4 px-4",
            PROFILE_DESKTOP.barInner,
            PROFILE_DESKTOP.barColumnRow,
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {withBackButton ? (
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            ) : null}
            <Skeleton
              className={cn("h-6 w-40 rounded", PROFILE_DESKTOP.barTitle)}
            />
          </div>
          <div className="flex items-center gap-2">{headerActions}</div>
        </div>
      </header>

      <div className={cn("h-14", PROFILE_DESKTOP.barSpacer)} />

      <div className={cn("mx-auto w-full max-w-3xl", PROFILE_DESKTOP.grid)}>
        <div className={PROFILE_DESKTOP.side}>
          <div
            className={cn(
              "relative isolate mx-auto aspect-square w-full max-w-[56dvh] overflow-hidden bg-surface sm:mt-4 sm:rounded-3xl wide:mt-0 wide:max-w-none",
              PROFILE_DESKTOP.hero,
            )}
          >
            <Skeleton className="h-full w-full rounded-none" />
          </div>

          {/* Desktop summary: name, counts, location/gender, bio, goals */}
          <div className="hidden flex-col gap-4 wide:flex">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-7.5 w-44 rounded" />
              <div className="flex gap-5">
                <Skeleton className="h-5 w-20 rounded" />
                <Skeleton className="h-5 w-16 rounded" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
              <div className="flex gap-4">
                <Skeleton className="h-5 w-20 rounded" />
                <Skeleton className="h-5 w-14 rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-3 border-t border-border pt-4 dark:border-glass-border-10">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-2/3 rounded" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6.5 w-16 rounded-full" />
                <Skeleton className="h-6.5 w-20 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        <div className={PROFILE_DESKTOP.main}>
          {/* The tab controls are static chrome, so keep them real while only
              the data-backed regions remain skeletons. */}
          <div
            className={cn(
              "sticky top-[calc(env(safe-area-inset-top)+3.5rem)] z-40 bg-gradient-to-b from-background/95 via-background/82 to-transparent px-2 py-2 backdrop-blur-md",
              PROFILE_DESKTOP.tabsBar,
            )}
          >
            <div className="mx-auto w-full max-w-3xl">
              <Tabs value={navigation.tab}>
                <TabsList className="flex w-full">
                  <TabsTrigger value="details" className="flex-1">
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="buzz" className="flex-1">
                    Buzz
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div className="mx-auto w-full max-w-3xl">
            {navigation.tab === "buzz" ? (
              <>
                <div className="flex gap-2 overflow-hidden px-3 pt-3 pb-2">
                  {[
                    "All",
                    "Posts",
                    "Sessions",
                    "Replies",
                    "Media",
                    "Stats",
                  ].map((label) => (
                    <Skeleton
                      key={label}
                      className="h-8 w-16 shrink-0 rounded-full"
                    />
                  ))}
                </div>
                <ProfileBuzzSkeleton view={navigation.filter} />
              </>
            ) : (
              <div
                className={cn(
                  "flex flex-col gap-6 p-3",
                  PROFILE_DESKTOP.details,
                )}
              >
                {/* Info pills (buddies / location / gender) — side column on desktop */}
                <div className="scrollbar-hide flex gap-2 overflow-x-auto py-1 wide:hidden">
                  <Skeleton className="h-9 w-28 shrink-0 rounded-full" />
                  <Skeleton className="h-9 w-32 shrink-0 rounded-full" />
                  <Skeleton className="h-9 w-24 shrink-0 rounded-full" />
                </div>

                {/* Activities section */}
                <div
                  className={cn(
                    "flex flex-col space-y-3",
                    PROFILE_DESKTOP.section,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-28 rounded" />
                  </div>
                  <div
                    className={cn(
                      "flex flex-col space-y-2",
                      PROFILE_DESKTOP.twoUpList,
                    )}
                  >
                    {[1, 2, 3, 4].map((itemKey) => (
                      <div
                        key={itemKey}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-2xl border border-border bg-surface-alt p-2",
                          // Three on a phone, as before; the fourth completes
                          // the desktop's second row.
                          itemKey === 4 && "hidden wide:flex",
                        )}
                      >
                        <Skeleton className="h-[60px] w-[60px] shrink-0 rounded-xl" />
                        <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                          <div className="flex flex-col gap-0.5 py-0.5">
                            <Skeleton className="h-4.5 w-32 rounded" />
                            <Skeleton className="h-4 w-20 rounded" />
                            <Skeleton className="h-4 w-16 rounded" />
                          </div>
                          <Skeleton className="mt-[3px] h-4 w-12 shrink-0 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Member of section (horizontal logos) */}
                <div
                  className={cn(
                    "flex flex-col space-y-3",
                    PROFILE_DESKTOP.section,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-20 rounded" />
                  </div>
                  <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-2">
                    {[1, 2, 3, 4].map((itemKey) => (
                      <div
                        key={itemKey}
                        className="flex w-14 flex-col items-center gap-1.5"
                      >
                        <Skeleton className="size-14 rounded-xl" />
                        <Skeleton className="h-3 w-12 rounded" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent events section */}
                <div
                  className={cn(
                    "flex flex-col space-y-3",
                    PROFILE_DESKTOP.section,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-28 rounded" />
                  </div>
                  <div className="flex flex-col gap-2 pb-2 wide:grid wide:content-start wide:@lg:grid-cols-2">
                    {[1, 2].map((itemKey) => (
                      <div
                        key={itemKey}
                        className="flex items-center gap-3 rounded-xl border border-border bg-surface-alt p-2"
                      >
                        <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                        <div className="min-w-0 flex-1 space-y-1">
                          <Skeleton className="h-4.5 w-40 rounded" />
                          <Skeleton className="h-3 w-16 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gallery photo card — one cell of the two-up grid on desktop */}
                <div
                  className={cn(
                    "relative aspect-square w-full overflow-hidden rounded-[32px] border border-border bg-surface shadow-lg wide:mx-auto wide:max-w-lg",
                    PROFILE_DESKTOP.photo,
                  )}
                >
                  <Skeleton className="h-full w-full rounded-none" />
                </div>

                {/* About me — side column on desktop. The heading row is h-8 in
                  the real view (room for the Add music button). */}
                <div className="flex flex-col space-y-3 wide:hidden">
                  <div className="flex h-8 items-center justify-between">
                    <Skeleton className="h-4 w-20 rounded" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4.5 w-full rounded" />
                    <Skeleton className="h-4.5 w-full rounded" />
                    <Skeleton className="h-4.5 w-3/4 rounded" />
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Skeleton className="h-7 w-20 rounded-full" />
                      <Skeleton className="h-7 w-16 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Music picks — under About me, like ProfileMusicSection: the
                  closed case on its stage (same container-unit formula), the
                  play bar with its service chips, then the song thumbnails. */}
                <div
                  className={cn(
                    "flex flex-col space-y-3",
                    PROFILE_DESKTOP.section,
                  )}
                >
                  <div className="flex h-7 items-center justify-between">
                    <Skeleton className="h-4 w-24 rounded" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <div
                      className="w-full"
                      style={{ containerType: "inline-size" }}
                    >
                      <div
                        className="flex items-center justify-center"
                        style={{ height: MUSIC_STAGE_HEIGHT_CSS }}
                      >
                        <Skeleton
                          className="aspect-square rounded-[5.5%]"
                          style={{ height: MUSIC_CASE_SIZE_CSS }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 rounded-[24px] border border-border bg-surface-alt px-4 py-3 dark:border-glass-border-10">
                      <div className="flex items-center gap-3">
                        <Skeleton className="size-11 shrink-0 rounded-full" />
                        <div className="flex flex-1 flex-col gap-1.5">
                          <Skeleton className="h-4.5 w-36 rounded" />
                          <Skeleton className="h-3.5 w-48 rounded" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Skeleton className="h-7.5 w-28 rounded-full" />
                        <Skeleton className="h-7.5 w-22 rounded-full" />
                        <Skeleton className="h-7.5 w-32 rounded-full" />
                      </div>
                    </div>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3].map((itemKey) => (
                        <Skeleton key={itemKey} className="size-9 rounded-md" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
